import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Heading as HeadingIcon, List, ListOrdered } from 'lucide-react';
import { toEditorContent, isEmptyContent } from '../lib/richText';

/**
 * Formatiertes Eingabefeld fuer einen einzelnen Listeneintrag der
 * Eingabekarten (Analyse, Durchfuehrung, Schwachstellen, Massnahmen).
 *
 * Ersetzt die frueheren `<textarea rows={2}>`: Der Berater kann eine
 * Ueberschrift, Absaetze und echte Aufzaehlungen eingeben und sieht seinen
 * vollstaendigen Text, weil das Feld mitwaechst.
 *
 * Gespeichert wird HTML. Bestandsdaten in reinem Text werden beim Laden
 * verlustfrei in Absaetze uebersetzt (siehe lib/richText).
 */

interface RichListInputProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  darkMode?: boolean;
  disabled?: boolean;
  /** Minimale sichtbare Hoehe. Default entspricht etwa zwei Textzeilen. */
  minHeight?: string;
}

export function RichListInput({
  value,
  onChange,
  placeholder = 'Text eingeben …',
  darkMode = false,
  disabled = false,
  minHeight = '3.5rem',
}: RichListInputProps) {
  // Der aktuelle Wert als Ref, damit onUpdate ihn ohne Neuaufbau des Editors
  // vergleichen kann.
  const valueRef = useRef(value);
  valueRef.current = value;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
    ],
    content: toEditorContent(value),
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // Beim Laden von Bestandsdaten (reiner Text) normalisiert TipTap den
      // Inhalt zu Absaetzen und meldet das als Aenderung. Das ist keine
      // Nutzereingabe und darf keinen Speichervorgang ausloesen.
      if (html === toEditorContent(valueRef.current)) return;
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'rich-list-input__content focus:outline-none px-4 py-2.5',
        style: `min-height: ${minHeight}`,
      },
    },
  });

  // Aenderungen von aussen uebernehmen (z. B. „Vorschlaege generieren“),
  // aber nie waehrend der Nutzer tippt.
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const next = toEditorContent(value);
    if (next !== editor.getHTML()) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  const showPlaceholder = isEmptyContent(value);

  const toolBtn = (active: boolean) =>
    [
      'p-1.5 rounded transition-colors',
      active
        ? darkMode
          ? 'bg-slate-600 text-white'
          : 'bg-slate-200 text-slate-900'
        : darkMode
          ? 'text-slate-400 hover:bg-slate-600 hover:text-white'
          : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900',
    ].join(' ');

  return (
    <div
      className={`flex-1 min-w-0 border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 ${
        darkMode ? 'bg-slate-600 border-slate-500 text-white' : 'bg-white border-slate-300 text-slate-900'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <style>{`
        .rich-list-input__content { font-size: 0.875rem; line-height: 1.55; }
        .rich-list-input__content:focus { outline: none; }
        .rich-list-input__content p { margin: 0.15em 0; }
        .rich-list-input__content h3 { font-size: 1em; font-weight: 700; margin: 0.4em 0 0.2em; }
        .rich-list-input__content ul { list-style-type: disc; padding-left: 1.4em; margin: 0.35em 0; }
        .rich-list-input__content ol { list-style-type: decimal; padding-left: 1.4em; margin: 0.35em 0; }
        .rich-list-input__content li { margin: 0.15em 0; }
        .rich-list-input__content li > p { margin: 0; }
      `}</style>

      <div
        className={`flex items-center gap-0.5 px-2 py-1 border-b ${
          darkMode ? 'border-slate-500 bg-slate-700/50' : 'border-slate-200 bg-slate-50'
        }`}
      >
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          disabled={disabled}
          className={toolBtn(editor.isActive('heading', { level: 3 }))}
          title="Überschrift"
          aria-label="Überschrift"
        >
          <HeadingIcon className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
          className={toolBtn(editor.isActive('bold'))}
          title="Fett"
          aria-label="Fett"
        >
          <Bold className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
          className={toolBtn(editor.isActive('bulletList'))}
          title="Aufzählung"
          aria-label="Aufzählung"
        >
          <List className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
          className={toolBtn(editor.isActive('orderedList'))}
          title="Nummerierte Liste"
          aria-label="Nummerierte Liste"
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="relative">
        {showPlaceholder && (
          <div
            className={`absolute top-2.5 left-4 pointer-events-none select-none text-sm ${
              darkMode ? 'text-slate-400' : 'text-slate-400'
            }`}
          >
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} translate="no" />
      </div>
    </div>
  );
}
